import { Shield, Eye, Lock, Users, FileText, Mail } from 'lucide-react';

const PrivacyPage = () => {
  const sections = [
    {
      icon: Eye,
      title: 'Information We Collect',
      content: [
        'Personal information you provide when creating an account (name, email address)',
        'Course enrollment and progress data',
        'Payment information (processed securely by third-party providers)',
        'Usage data and analytics to improve our services',
        'Communication preferences and support interactions'
      ]
    },
    {
      icon: Lock,
      title: 'How We Use Your Information',
      content: [
        'Provide and deliver our educational courses and services',
        'Process payments and manage your account',
        'Send important updates about courses and your account',
        'Improve our platform and develop new features',
        'Provide customer support and respond to inquiries'
      ]
    },
    {
      icon: Shield,
      title: 'Data Protection',
      content: [
        'We use industry-standard security measures to protect your data',
        'All payment processing is handled by secure, PCI-compliant providers',
        'Your personal information is encrypted both in transit and at rest',
        'We regularly review and update our security practices',
        'Access to your data is limited to authorized personnel only'
      ]
    },
    {
      icon: Users,
      title: 'Information Sharing',
      content: [
        'We do not sell your personal information to third parties',
        'Course completion data may be shared with employers (with your consent)',
        'We may share aggregated, non-personal data for research purposes',
        'Service providers may access data necessary to perform their functions',
        'Legal compliance may require disclosure in certain circumstances'
      ]
    },
    {
      icon: FileText,
      title: 'Your Rights',
      content: [
        'Access and review your personal information',
        'Request correction of inaccurate or incomplete data',
        'Request deletion of your account and associated data',
        'Opt-out of marketing communications at any time',
        'Data portability rights where applicable'
      ]
    },
    {
      icon: Mail,
      title: 'Contact Us',
      content: [
        'Questions about this privacy policy should be directed to karthik@techgeek.co.in',
        'We will respond to privacy-related inquiries within 30 days',
        'You can also contact us through our support page',
        'Our data protection officer is available for serious privacy concerns'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-green-100 to-emerald-100 bg-clip-text text-transparent">Privacy</span> Policy
            </h1>
            <p className="text-xl text-green-50 max-w-3xl mx-auto">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
            <div className="mt-6 text-green-100">
              <p>Last updated: January 2024</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Introduction */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Our Commitment to <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Privacy</span>
          </h2>
          <p className="text-gray-600 leading-relaxed">
            At ExecuteAutomation, we are committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use our 
            educational platform and services. We believe in transparency and want you to understand exactly how your data is handled.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <div className="flex items-center mb-6">
                  <div className="p-3 bg-green-100 rounded-xl mr-4">
                    <Icon className="h-6 w-6 text-green-600" aria-label="Icon icon" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                </div>
                <ul className="space-y-3">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Updates Notice */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-8 text-white mt-12">
          <h3 className="text-xl font-bold mb-4">Policy Updates</h3>
          <p className="text-green-100 mb-4">
            We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. 
            We will notify you of any significant changes by posting the updated policy on our website and updating the 
            "Last updated" date.
          </p>
          <p className="text-green-100">
            Your continued use of our services after any changes indicates your acceptance of the updated Privacy Policy.
          </p>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mt-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Questions About <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Privacy</span>?
          </h3>
          <p className="text-gray-600 mb-6">
            If you have any questions about this Privacy Policy or our data practices, please don't hesitate to contact us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:karthik@techgeek.co.in"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all"
            >
              <Mail className="h-5 w-5 mr-2" aria-label="Mail icon" />
              Email Us
            </a>
            <a 
              href="/contact"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-green-500 hover:text-green-600 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;